/**
 * ISMS Scope Page — ISO/IEC 27001:2022 clause 4.3
 *
 * A structured document editor for the ISMS Scope, tying together live data
 * from across the Nimbus platform. The document follows the 13-section
 * structure recommended by ISO/IEC 27001 auditors.
 *
 * Architecture:
 *  - Single document per tenant stored via the isms-scope Lambda endpoint
 *  - Reference panels pull live counts from existing platform APIs
 *  - Status workflow: draft → in_review → approved
 *  - Sticky left-hand TOC with section completion indicators
 *  - Export to PDF via jsPDF
 *
 * Sections:
 *  1.  Purpose
 *  2.  Organisational Context
 *  3.  Scope Statement
 *  4.  Organisational Boundaries
 *  5.  Physical Scope
 *  6.  Information Assets
 *  7.  Technology Scope
 *  8.  Risk Management Framework
 *  9.  Control Framework
 *  10. Compliance & Governance
 *  11. ISMS Records & Evidence
 *  12. Interfaces & Dependencies
 *  13. Exclusions
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import {
  assetsApi,
  controlMatrixApi,
  departmentsApi,
  evidenceRegistersApi,
  interestedPartiesApi,
  ismsScopeApi,
  issueRegistersApi,
  legalRegisterApi,
  peopleApi,
  pestleApi,
  physicalSitesApi,
  registersApi,
  remediationRegistersApi,
  securityObjectivesApi,
  threatRegistersApi,
} from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScopeDoc {
  tenantId?: string;
  documentId?: string;
  status: 'draft' | 'in_review' | 'approved';
  version: number;
  s1_ismsPurpose: string;
  s1_iso27001Alignment: string;
  s2_notes: string;
  s3_services: string;
  s3_departments: string;
  s3_locations: string;
  s3_technology: string;
  s3_useCustom: boolean;
  s3_customStatement: string;
  s4_notes: string;
  s5_notes: string;
  s5_additionalSites: string;
  s6_dataTypes: string;
  s6_notes: string;
  s7_systems: string;
  s7_networks: string;
  s7_infrastructure: string;
  s7_cloudPlatforms: string;
  s8_notes: string;
  s9_notes: string;
  s10_notes: string;
  s11_notes: string;
  s12_thirdParties: string;
  s12_suppliers: string;
  s12_externalSystems: string;
  s12_thirdPartyRefs: { id: string; name: string; category?: string }[];
  s12_supplierRefs: { id: string; name: string; category?: string }[];
  s12_externalSystemRefs: { id: string; name: string; assetType?: string }[];
  s13_exclusions: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTION_IDS = [
  'sec-1', 'sec-2', 'sec-3', 'sec-4', 'sec-5', 'sec-6', 'sec-7',
  'sec-8', 'sec-9', 'sec-10', 'sec-11', 'sec-12', 'sec-13',
] as const;

const SECTIONS = [
  { id: 'sec-1',  num: '1',  title: 'Purpose',                   iso: 'ISO 27001 §4.1',    icon: 'ri-flag-2-line' },
  { id: 'sec-2',  num: '2',  title: 'Organisational Context',    iso: 'ISO 27001 §4.1–4.2',icon: 'ri-organisation-chart' },
  { id: 'sec-3',  num: '3',  title: 'Scope Statement',            iso: 'ISO 27001 §4.3',    icon: 'ri-focus-3-line' },
  { id: 'sec-4',  num: '4',  title: 'Organisational Boundaries', iso: 'ISO 27001 §4.3',    icon: 'ri-group-line' },
  { id: 'sec-5',  num: '5',  title: 'Physical Scope',             iso: 'ISO 27001 §4.3',    icon: 'ri-building-2-line' },
  { id: 'sec-6',  num: '6',  title: 'Information Assets',         iso: 'ISO 27001 §8.1',    icon: 'ri-database-2-line' },
  { id: 'sec-7',  num: '7',  title: 'Technology Scope',           iso: 'ISO 27001 §8.x',    icon: 'ri-server-2-line' },
  { id: 'sec-8',  num: '8',  title: 'Risk Management Framework', iso: 'ISO 27001 §6.1',    icon: 'ri-alert-line' },
  { id: 'sec-9',  num: '9',  title: 'Control Framework',          iso: 'ISO 27001 §6.1.3',  icon: 'ri-shield-check-line' },
  { id: 'sec-10', num: '10', title: 'Compliance & Governance',   iso: 'ISO 27001 §4.2',    icon: 'ri-scales-3-line' },
  { id: 'sec-11', num: '11', title: 'ISMS Records & Evidence',   iso: 'ISO 27001 §7.5',    icon: 'ri-file-list-3-line' },
  { id: 'sec-12', num: '12', title: 'Interfaces & Dependencies', iso: 'ISO 27001 §8.29',   icon: 'ri-git-branch-line' },
  { id: 'sec-13', num: '13', title: 'Exclusions',                 iso: 'ISO 27001 §4.3',    icon: 'ri-forbid-line' },
] as const;

const EMPTY: ScopeDoc = {
  status: 'draft', version: 0,
  s1_ismsPurpose: '', s1_iso27001Alignment: '',
  s2_notes: '',
  s3_services: '', s3_departments: '', s3_locations: '', s3_technology: '',
  s3_useCustom: false, s3_customStatement: '',
  s4_notes: '',
  s5_notes: '', s5_additionalSites: '',
  s6_dataTypes: '', s6_notes: '',
  s7_systems: '', s7_networks: '', s7_infrastructure: '', s7_cloudPlatforms: '',
  s8_notes: '', s9_notes: '', s10_notes: '', s11_notes: '',
  s12_thirdParties: '', s12_suppliers: '', s12_externalSystems: '',
  s12_thirdPartyRefs: [], s12_supplierRefs: [], s12_externalSystemRefs: [],
  s13_exclusions: '',
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_META: Record<string, { label: string; badge: string; next: string; nextLabel: string; nextIcon: string }> = {
  draft:     { label: 'Draft',            badge: 'bg-label-warning',  next: 'in_review', nextLabel: 'Submit for Review', nextIcon: 'ri-send-plane-line' },
  in_review: { label: 'Under Review',     badge: 'bg-label-info',     next: 'approved',  nextLabel: 'Approve',           nextIcon: 'ri-check-double-line' },
  approved:  { label: 'Approved',         badge: 'bg-label-success',  next: 'draft',     nextLabel: 'Revert to Draft',   nextIcon: 'ri-edit-line' },
};

// ---------------------------------------------------------------------------
// Completion scoring per section (a section is "complete" if at least one
// relevant field is non-empty)
// ---------------------------------------------------------------------------

function sectionComplete(doc: ScopeDoc, sectionNum: string): boolean {
  switch (sectionNum) {
    case '1':  return !!(doc.s1_ismsPurpose || doc.s1_iso27001Alignment);
    case '2':  return !!doc.s2_notes;
    case '3':  return !!(doc.s3_customStatement || doc.s3_services || doc.s3_departments);
    case '4':  return !!doc.s4_notes;
    case '5':  return !!(doc.s5_notes || doc.s5_additionalSites);
    case '6':  return !!(doc.s6_dataTypes || doc.s6_notes);
    case '7':  return !!(doc.s7_systems || doc.s7_networks || doc.s7_infrastructure || doc.s7_cloudPlatforms);
    case '8':  return !!doc.s8_notes;
    case '9':  return !!doc.s9_notes;
    case '10': return !!doc.s10_notes;
    case '11': return !!doc.s11_notes;
    case '12': return !!(doc.s12_thirdParties || doc.s12_suppliers || doc.s12_externalSystems || doc.s12_thirdPartyRefs?.length || doc.s12_supplierRefs?.length || doc.s12_externalSystemRefs?.length);
    case '13': return !!doc.s13_exclusions;
    default:   return false;
  }
}

// ---------------------------------------------------------------------------
// Reference panel sub-component
// ---------------------------------------------------------------------------

interface RefItem { label: string; count: number; to: string; icon: string; loading: boolean }

const ReferencePanel: React.FC<{ items: RefItem[] }> = ({ items }) => (
  <div className="row g-2 mb-3">
    {items.map((item) => (
      <div key={item.to} className="col-sm-6 col-lg-4">
        <Link to={item.to} className="text-decoration-none">
          <div className="np-card h-100" style={{ transition: 'box-shadow 0.15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--np-shadow-md)')}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}>
            <div className="np-card-body py-2 px-3 d-flex align-items-center gap-2">
              <i className={`${item.icon} text-primary fs-5`} />
              <div className="flex-grow-1 min-width-0">
                <div className="text-truncate small fw-medium text-body">{item.label}</div>
                {item.loading ? (
                  <span className="np-chip np-tone-neutral">...</span>
                ) : (
                  <span className="np-chip np-tone-primary">{item.count} record{item.count !== 1 ? 's' : ''}</span>
                )}
              </div>
              <i className="ri-arrow-right-s-line" style={{ color: 'var(--bs-secondary-color)' }} />
            </div>
          </div>
        </Link>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Labelled textarea
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  hint?: string;
  value: string;
  rows?: number;
  placeholder?: string;
  onChange: (v: string) => void;
}

const TextareaField: React.FC<FieldProps> = ({ label, hint, value, rows = 4, placeholder, onChange }) => (
  <div className="np-field">
    <label>{label}</label>
    {hint && <div className="np-hint mb-1">{hint}</div>}
    <textarea
      className="np-textarea"
      rows={rows}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ---------------------------------------------------------------------------
// Section card wrapper
// ---------------------------------------------------------------------------

interface SectionCardProps {
  id: string;
  num: string;
  title: string;
  iso: string;
  icon: string;
  complete: boolean;
  children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({ id, num, title, iso, icon, complete, children }) => (
  <div id={id} className="np-card mb-4">
    <div className="np-card-head">
      <span className={`np-step-badge ${complete ? 'np-tone-success' : ''}`}>
        {complete ? <i className="ri-check-line" /> : num}
      </span>
      <div className="flex-grow-1">
        <h2>{title}</h2>
        <span className="np-hint"><i className="ri-bookmark-3-line me-1" />{iso}</span>
      </div>
      <i className={`${icon} fs-5`} style={{ color: 'var(--bs-secondary-color)' }} />
    </div>
    <div className="np-card-body pt-4">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Scope sentence preview
// ---------------------------------------------------------------------------

const ScopeSentencePreview: React.FC<{
  services: string; departments: string; locations: string; technology: string;
}> = ({ services, departments, locations, technology }) => {
  const parts = [
    services     || '[service or activities]',
    departments  || '[departments]',
    locations    || '[locations]',
    technology   || '[technology platforms]',
  ];
  const sentence = `The ISMS covers the development, operation and support of ${parts[0]} delivered by ${parts[1]} from ${parts[2]} using ${parts[3]}.`;

  return (
    <div className="np-banner np-tone-primary mb-3">
      <i className="ri-eye-line" />
      <div>
        <div className="fw-semibold small mb-1">Live Scope Sentence Preview</div>
        <div className="small" style={{ fontStyle: 'italic' }}>{sentence}</div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ISMSScopePage: React.FC = () => {
  const qc        = useQueryClient();
  const toast     = useToast();
  const swal      = useSwal();
  const hasUnsavedRef   = useRef(false);
  const sectionsRef     = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  // Active section tracking for TOC
  const [activeSection, setActiveSection] = useState<string>('sec-1');

  // Local form state (mirrors server state; edited independently until Save)
  const [form, setForm] = useState<ScopeDoc>(EMPTY);
  const [isDirty, setIsDirty] = useState(false);

  // Section 12 typeahead state
  const [tpSearch,     setTpSearch    ] = useState('');
  const [suppSearch,   setSuppSearch  ] = useState('');
  const [extSearch,    setExtSearch   ] = useState('');
  const [showTpDrop,   setShowTpDrop  ] = useState(false);
  const [showSuppDrop, setShowSuppDrop] = useState(false);
  const [showExtDrop,  setShowExtDrop ] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: scopeData, isLoading: scopeLoading } = useQuery({
    queryKey: ['isms-scope'],
    queryFn: () => ismsScopeApi.get(),
  });

  // Reference counts
  const { data: pestleList   = [], isLoading: loadingPestle   } = useQuery({ queryKey: ['pestle'], queryFn: async () => { const r = await pestleApi.list(); return r.assessments || []; } });
  const { data: parties      = [], isLoading: loadingParties  } = useQuery({ queryKey: ['interested-parties'], queryFn: () => interestedPartiesApi.getAll() });
  const { data: legal        = [], isLoading: loadingLegal    } = useQuery({ queryKey: ['legal-register'], queryFn: () => legalRegisterApi.getAll() });
  const { data: people       = [], isLoading: loadingPeople   } = useQuery({ queryKey: ['people'], queryFn: () => peopleApi.getAll() });
  const { data: departments  = [], isLoading: loadingDepts    } = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.getAll() });
  const { data: sites        = [], isLoading: loadingSites    } = useQuery({ queryKey: ['physical-sites'], queryFn: () => physicalSitesApi.getAll() });
  const { data: assets       = [], isLoading: loadingAssets   } = useQuery({ queryKey: ['assets'], queryFn: () => assetsApi.getAll() });
  const { data: riskRegs     = [], isLoading: loadingRiskR    } = useQuery({ queryKey: ['registers-risk'], queryFn: () => registersApi.getAll() });
  const { data: threatRegs   = [], isLoading: loadingThreatR  } = useQuery({ queryKey: ['threat-registers'], queryFn: () => threatRegistersApi.getAll() });
  const { data: remRegs      = [], isLoading: loadingRemR     } = useQuery({ queryKey: ['remediation-registers'], queryFn: () => remediationRegistersApi.getAll() });
  const { data: matrices     = [], isLoading: loadingMatrices } = useQuery({ queryKey: ['control-matrices'], queryFn: () => controlMatrixApi.getAll() });
  const { data: secObjs      = [], isLoading: loadingSecObjs  } = useQuery({ queryKey: ['security-objectives'], queryFn: () => securityObjectivesApi.getAll() });
  const { data: evidenceRegs = [], isLoading: loadingEvRegs   } = useQuery({ queryKey: ['evidence-registers'], queryFn: () => evidenceRegistersApi.getAll() });
  const { data: issueRegs    = [], isLoading: loadingIssRegs  } = useQuery({ queryKey: ['issue-registers'], queryFn: () => issueRegistersApi.getAll() });

  // Initialise form from server data
  useEffect(() => {
    if (scopeData?.scope) {
      setForm({ ...EMPTY, ...scopeData.scope });
      setIsDirty(false);
    }
  }, [scopeData]);

  // Update dirty ref for beforeunload guard
  useEffect(() => { hasUnsavedRef.current = isDirty; }, [isDirty]);

  // Warn before navigating away with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Intersect observer for TOC highlight
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [scopeLoading]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (data: ScopeDoc) => ismsScopeApi.save(data),
    onSuccess: (res) => {
      if (res?.scope) setForm(res.scope);
      setIsDirty(false);
      qc.invalidateQueries({ queryKey: ['isms-scope'] });
      toast.success('ISMS Scope updated.');
    },
    onError: () => toast.error('Failed to save ISMS Scope.'),
  });

  const publishMutation = useMutation({
    mutationFn: () => ismsScopeApi.publish(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['isms-scope'] });
      toast.success(`Status changed to ${res.newStatus.replace('_', ' ')}.`);
    },
    onError: () => toast.error('Failed to update status.'),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const update = useCallback(<K extends keyof ScopeDoc>(key: K, value: ScopeDoc[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  }, []);

  const handleSave = () => saveMutation.mutate(form);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // ── Build a fully self-contained print DOM with only inline styles ──
      // (html2canvas crashes on oklch/color() from the Materio theme CSS)
      const ACCENT  = '#4f46e5';
      const done    = SECTIONS.filter(s => sectionComplete(form, s.num)).length;
      const pct     = Math.round((done / SECTIONS.length) * 100);
      const sLabels: Record<string,string> = { draft:'Draft', in_review:'Under Review', approved:'Approved' };
      const sColors: Record<string,string> = { draft:'#d97706', in_review:'#0891b2', approved:'#15803d' };
      const sBg:     Record<string,string>  = { draft:'#fef3c7', in_review:'#cffafe', approved:'#dcfce7' };
      const statusLabel = sLabels[form.status] ?? form.status;
      const statusColor = sColors[form.status] ?? ACCENT;
      const statusBg    = sBg[form.status]     ?? '#f3f4f6';
      const lastSaved   = form.updatedAt
        ? new Date(form.updatedAt).toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })
        : 'Not yet saved';

      const scopeSentence = form.s3_useCustom && form.s3_customStatement.trim()
        ? form.s3_customStatement.trim()
        : `The ISMS covers the development, operation and support of ${form.s3_services||'[services]'} delivered by ${form.s3_departments||'[departments]'} from ${form.s3_locations||'[locations]'} using ${form.s3_technology||'[technology]'}.`;

      const cs = (s: string) => s
        ? s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
        : '<span style="color:#9ca3af">—</span>';

      // ── Helper: reference count card (2-per-row) ────────────────────────
      const refCard = (label: string, count: number) =>
        `<div style="display:inline-block;width:calc(50% - 12px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;">
          <span style="font-size:20px;font-weight:700;color:#111827;">${count}</span>
          <span style="font-size:12px;color:#6b7280;margin-left:8px;">${label}</span>
        </div>`;

      // ── Helper: labelled field block ────────────────────────────────────
      const field = (label: string, value: string) =>
        `<div style="margin-bottom:14px;">
          <div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;">${label}</div>
          <div style="font-size:13px;color:#111827;line-height:1.6;white-space:pre-wrap;">${cs(value)}</div>
        </div>`;

      // ── Helper: linked-item table (uses <table> for html2canvas compat) ──
      const tableHtml = (
        items: { name: string; [k: string]: any }[],
        col2key: string,
        col2label: string,
      ) => {
        if (!items?.length)
          return `<p style="font-size:12px;color:#9ca3af;margin:0 0 10px 0;font-style:italic;">None linked</p>`;
        const rows = items.map((it) =>
          `<tr>
            <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#111827;">${cs(it.name)}</td>
            <td style="padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#6b7280;">${cs(it[col2key] || '—')}</td>
          </tr>`,
        ).join('');
        return (
          `<table style="width:100%;border-collapse:collapse;margin-bottom:10px;border:1px solid #e5e7eb;">` +
          `<thead><tr>` +
          `<th style="padding:5px 8px;background:#f9fafb;font-size:11px;font-weight:700;color:#374151;text-align:left;">Name</th>` +
          `<th style="padding:5px 8px;background:#f9fafb;font-size:11px;font-weight:700;color:#374151;text-align:left;">${col2label}</th>` +
          `</tr></thead><tbody>${rows}</tbody></table>`
        );
      };

      // ── Helper: section card ────────────────────────────────────────────
      const section = (num: string, title: string, iso: string, body: string) => {
        const complete = sectionComplete(form, num);
        return `
        <div style="margin-bottom:24px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;break-inside:avoid;">
          <div style="display:flex;align-items:center;gap:12px;padding:12px 16px;background:#f9fafb;border-bottom:1px solid #e5e7eb;">
            <div style="width:28px;height:28px;border-radius:50%;background:${complete ? '#15803d' : ACCENT};color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              ${complete ? '✓' : num}
            </div>
            <div style="flex:1;">
              <div style="font-size:14px;font-weight:700;color:#111827;">${title}</div>
              <div style="font-size:11px;color:#6b7280;">${iso}</div>
            </div>
            ${complete ? `<div style="font-size:11px;color:#15803d;font-weight:600;">Complete</div>` : `<div style="font-size:11px;color:#9ca3af;">Incomplete</div>`}
          </div>
          <div style="padding:16px 20px;">${body}</div>
        </div>`;
      };

      const html = `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:860px;padding:40px;">

        <!-- Cover -->
        <div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
          <h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 12px 0;">ISMS Scope Document</h1>
          <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:24px;">
            <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:${statusColor};background:${statusBg};">${statusLabel}</span>
            <span style="font-size:13px;color:#6b7280;">Version ${form.version}</span>
            <span style="font-size:13px;color:#6b7280;">·</span>
            <span style="font-size:13px;color:#6b7280;">Last saved: ${lastSaved}</span>
          </div>

          <!-- Completion bar -->
          <div style="margin-bottom:20px;">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px;">
              <span style="font-size:12px;font-weight:600;color:#374151;">Document Completion</span>
              <span style="font-size:12px;font-weight:700;color:${pct===100?'#15803d':ACCENT};">${pct}% (${done}/${SECTIONS.length} sections)</span>
            </div>
            <div style="height:8px;background:#e5e7eb;border-radius:4px;overflow:hidden;">
              <div style="height:100%;width:${pct}%;background:${pct===100?'#15803d':ACCENT};border-radius:4px;"></div>
            </div>
          </div>

          <!-- Reference data grid -->
          <div style="font-size:12px;font-weight:700;color:#374151;margin-bottom:10px;">Connected Platform Data</div>
          <div>
            ${refCard('PESTLE Assessments', pestleList.length)}
            ${refCard('Interested Parties', parties.length)}
            ${refCard('Legal & Regulatory', legal.length)}
            ${refCard('People', people.length)}
            ${refCard('Departments', departments.length)}
            ${refCard('Physical Sites', sites.length)}
            ${refCard('Information Assets', assets.length)}
            ${refCard('Risk Registers', riskRegs.length)}
            ${refCard('Threat Registers', threatRegs.length)}
            ${refCard('Remediation Registers', remRegs.length)}
            ${refCard('Control Matrices', matrices.length)}
            ${refCard('Security Objectives', secObjs.length)}
            ${refCard('Evidence Registers', evidenceRegs.length)}
            ${refCard('Issue Registers', issueRegs.length)}
          </div>
        </div>

        <!-- Sections -->
        ${section('1','Purpose','ISO 27001 §4.1',
          field('ISMS Purpose', form.s1_ismsPurpose) +
          field('ISO 27001 Alignment Statement', form.s1_iso27001Alignment)
        )}
        ${section('2','Organisational Context','ISO 27001 §4.1–4.2',
          `<div style="margin-bottom:12px;">
            ${refCard('PESTLE Assessments', pestleList.length)}
            ${refCard('Interested Parties', parties.length)}
            ${refCard('Legal & Regulatory', legal.length)}
          </div>` +
          field('Context Notes', form.s2_notes)
        )}
        ${section('3','Scope Statement','ISO 27001 §4.3',
          field('Scope Statement', scopeSentence) +
          (!form.s3_useCustom
            ? field('Services / Activities', form.s3_services) +
              field('Departments', form.s3_departments) +
              field('Locations', form.s3_locations) +
              field('Technology Platforms', form.s3_technology)
            : '')
        )}
        ${section('4','Organisational Boundaries','ISO 27001 §4.3',
          `<div style="margin-bottom:12px;">
            ${refCard('People', people.length)}
            ${refCard('Departments', departments.length)}
          </div>` +
          field('Boundary Notes', form.s4_notes)
        )}
        ${section('5','Physical Scope','ISO 27001 §4.3',
          `<div style="margin-bottom:12px;">${refCard('Physical Sites', sites.length)}</div>` +
          field('Additional Sites / Hosting Environments', form.s5_additionalSites) +
          field('Physical Scope Notes', form.s5_notes)
        )}
        ${section('6','Information Assets','ISO 27001 §8.1',
          `<div style="margin-bottom:12px;">${refCard('Registered Information Assets', assets.length)}</div>` +
          field('Data Types Handled', form.s6_dataTypes) +
          field('Additional Notes', form.s6_notes)
        )}
        ${section('7','Technology Scope','ISO 27001 §8.x',
          field('Systems', form.s7_systems) +
          field('Networks', form.s7_networks) +
          field('Infrastructure', form.s7_infrastructure) +
          field('Cloud Platforms', form.s7_cloudPlatforms)
        )}
        ${section('8','Risk Management Framework','ISO 27001 §6.1',
          `<div style="margin-bottom:12px;">
            ${refCard('Risk Registers', riskRegs.length)}
            ${refCard('Threat Registers', threatRegs.length)}
            ${refCard('Remediation Registers', remRegs.length)}
          </div>` +
          field('Risk Management Notes', form.s8_notes)
        )}
        ${section('9','Control Framework','ISO 27001 §6.1.3',
          `<div style="margin-bottom:12px;">
            ${refCard('Control Matrices', matrices.length)}
            ${refCard('Security Objectives', secObjs.length)}
          </div>` +
          field('Control Framework Notes', form.s9_notes)
        )}
        ${section('10','Compliance & Governance','ISO 27001 §4.2',
          `<div style="margin-bottom:12px;">
            ${refCard('Legal & Compliance Items', legal.length)}
            ${refCard('Issue Registers', issueRegs.length)}
          </div>` +
          field('Compliance & Governance Notes', form.s10_notes)
        )}
        ${section('11','ISMS Records & Evidence','ISO 27001 §7.5',
          `<div style="margin-bottom:12px;">${refCard('Evidence Registers', evidenceRegs.length)}</div>` +
          field('Records & Evidence Notes', form.s11_notes)
        )}
        ${section('12','Interfaces & Dependencies','ISO 27001 §8.29',
          `<div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:6px;">Third Parties</div>` +
          tableHtml(form.s12_thirdPartyRefs, 'category', 'Category') +
          field('Third Party Notes', form.s12_thirdParties) +
          `<div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;margin:12px 0 6px 0;">Suppliers</div>` +
          tableHtml(form.s12_supplierRefs, 'category', 'Category') +
          field('Supplier Notes', form.s12_suppliers) +
          `<div style="font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;margin:12px 0 6px 0;">External Systems</div>` +
          tableHtml(form.s12_externalSystemRefs, 'assetType', 'Type') +
          field('External Systems Notes', form.s12_externalSystems)
        )}
        ${section('13','Exclusions','ISO 27001 §4.3',
          field('Exclusion Statement', form.s13_exclusions)
        )}

        <div style="margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;display:flex;justify-content:space-between;">
          <span>ISMS Scope Document  ·  ISO/IEC 27001:2022 §4.3</span>
          <span>Generated ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}</span>
        </div>
      </div>`;

      // ── Mount off-screen, capture, remove ──────────────────────────────
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:860px;';
      wrapper.innerHTML = html;
      document.body.appendChild(wrapper);

      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 860,
        windowWidth: 860,
      });
      document.body.removeChild(wrapper);

      // ── Single continuous page sized to fit the full canvas ────────────
      const margin  = 10;
      const printW  = 190;                          // mm — A4 width minus margins
      const imgW    = canvas.width;
      const imgH    = canvas.height;
      const ratio   = printW / (imgW / 2);          // ÷2 because scale:2
      const docH    = (imgH / 2) * ratio + margin * 2;

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, docH] });

      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        margin, margin,
        printW, (imgH / 2) * ratio,
      );

      pdf.save(`ISMS-Scope-v${form.version}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to generate PDF.');
    } finally {
      setExporting(false);
    }
  };

  const handlePublish = async () => {
    const meta = STATUS_META[form.status];
    const confirmed = await swal.confirm(
      meta.nextLabel,
      `Move this document from "${meta.label}" to "${STATUS_META[meta.next]?.label ?? meta.next}"?`,
    );
    if (confirmed) publishMutation.mutate();
  };

  // ── Scroll helper ─────────────────────────────────────────────────────────

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  };

  // ── Loading guard ─────────────────────────────────────────────────────────

  if (scopeLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 320 }}>
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  const statusMeta    = STATUS_META[form.status] ?? STATUS_META.draft;
  const completedCount = SECTIONS.filter((s) => sectionComplete(form, s.num)).length;
  const pct = Math.round((completedCount / SECTIONS.length) * 100);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <PageHeader
        title="ISMS Scope"
        icon="ri-focus-3-line"
        subtitle={`ISO 27001 §4.3 — Status: ${statusMeta.label}  |  Version ${form.version}${form.updatedAt ? '  |  Last saved ' + new Date(form.updatedAt).toLocaleString() : ''}`}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {isDirty && (
              <span className="np-chip np-tone-warning align-self-center">
                <i className="ri-error-warning-line" />Unsaved changes
              </span>
            )}
            <button
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={handleExportPDF}
              disabled={exporting}
              title="Export to PDF"
            >
              {exporting
                ? <><span className="spinner-border spinner-border-sm me-1" />Generating…</>
                : <><i className="ri-file-pdf-line" />Export PDF</>}
            </button>
            <button
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={handlePublish}
              disabled={publishMutation.isPending || !scopeData?.exists}
              title={statusMeta.nextLabel}
            >
              <i className={statusMeta.nextIcon} />{statusMeta.nextLabel}
            </button>
            <button
              className="np-btn np-btn-primary np-btn-sm"
              onClick={handleSave}
              disabled={saveMutation.isPending || !isDirty}
            >
              {saveMutation.isPending
                ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                : <><i className="ri-save-line" />Save</>}
            </button>
          </div>
        }
      />

      {/* ── Overall progress bar ────────────────────────────────────────── */}
      <div className="np-card mb-4">
        <div className="np-card-body py-2 px-4">
          <div className="d-flex align-items-center gap-3">
            <span className="np-sub np-tabular" style={{ whiteSpace: 'nowrap' }}>
              {completedCount} / {SECTIONS.length} sections
            </span>
            <div className="np-progress flex-grow-1">
              <div
                className={`np-fill ${pct === 100 ? 'np-tone-success' : ''}`}
                style={{ width: `${pct}%`, transition: 'width 0.3s' }}
              />
            </div>
            <span className="np-sub np-tabular" style={{ whiteSpace: 'nowrap' }}>{pct}% complete</span>
          </div>
        </div>
      </div>

      <div className="np-detail-layout">

        {/* ── Sticky sidebar TOC ──────────────────────────────────────────── */}
        <div className="np-meta-rail">
          <div className="np-card">
            <div className="np-card-head">
              <i className="ri-list-check-2"></i>
              <h2>Sections</h2>
            </div>
            <div className="np-nav-list">
              {SECTIONS.map((s) => {
                const done   = sectionComplete(form, s.num);
                const active = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => scrollTo(s.id)}
                    className={`np-nav-item ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                  >
                    <i className={`${done ? 'ri-checkbox-circle-fill' : 'ri-circle-line'} np-nav-item-icon`}></i>
                    <div className="np-nav-item-body">
                      <div className="np-nav-item-title">{s.num}. {s.title}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Main content ────────────────────────────────────────────────── */}
        <div ref={sectionsRef}>

          {/* ── Section 1 – Purpose ───────────────────────────────────────── */}
          <SectionCard {...SECTIONS[0]} complete={sectionComplete(form, '1')}>
            <p className="np-sub mb-3">
              Define the objectives of the ISMS and its alignment with ISO/IEC 27001:2022.
              This section is used as the executive summary in audit proceedings.
            </p>
            <TextareaField
              label="Purpose of the ISMS"
              hint="State the reasons the organisation has established an ISMS and the business value it delivers."
              value={form.s1_ismsPurpose}
              placeholder="e.g. To protect the confidentiality, integrity and availability of information assets in support of business objectives and regulatory obligations..."
              rows={5}
              onChange={(v) => update('s1_ismsPurpose', v)}
            />
            <TextareaField
              label="Alignment with ISO/IEC 27001:2022"
              hint="Describe how the ISMS is aligned with the standard and any applicable frameworks."
              value={form.s1_iso27001Alignment}
              placeholder="e.g. The ISMS has been designed to meet the requirements of ISO/IEC 27001:2022 and incorporates controls from Annex A..."
              rows={4}
              onChange={(v) => update('s1_iso27001Alignment', v)}
            />
          </SectionCard>

          {/* ── Section 2 – Organisational Context ───────────────────────── */}
          <SectionCard {...SECTIONS[1]} complete={sectionComplete(form, '2')}>
            <p className="np-sub mb-3">
              References to the contextual analysis artefacts that underpin the ISMS Scope.
              Add notes to document your contextual approach.
            </p>
            <ReferencePanel items={[
              { label: 'PESTLE Analyses',       count: pestleList.length,  to: '/pestle',             icon: 'ri-global-line',          loading: loadingPestle },
              { label: 'Interested Parties',     count: parties.length,     to: '/interested-parties', icon: 'ri-team-line',             loading: loadingParties },
              { label: 'Legal & Regulatory',     count: legal.length,       to: '/legal-register',     icon: 'ri-government-line',       loading: loadingLegal },
            ]} />
            <TextareaField
              label="Contextual notes"
              hint="Summarise the key internal and external issues identified that are relevant to the ISMS."
              value={form.s2_notes}
              placeholder="e.g. A PESTLE analysis was conducted in Q1 2026 identifying key risks from regulatory change (NIS2, GDPR) and supply chain dependencies..."
              rows={5}
              onChange={(v) => update('s2_notes', v)}
            />
          </SectionCard>

          {/* ── Section 3 – Scope Statement ──────────────────────────────── */}
          <SectionCard {...SECTIONS[2]} complete={sectionComplete(form, '3')}>
            <p className="np-sub mb-3">
              The scope statement is the single most important clause in an ISO 27001 ISMS.
              Use the structured builder below or write a custom statement.
            </p>

            <div className="d-flex align-items-center gap-2 mb-3">
              <label className="np-switch mb-0">
                <input type="checkbox" role="switch" id="scopeCustomSwitch"
                  checked={form.s3_useCustom}
                  onChange={(e) => update('s3_useCustom', e.target.checked)} />
                <span className="np-switch-track"></span>
                <span className="np-switch-thumb"></span>
              </label>
              <label htmlFor="scopeCustomSwitch" className="mb-0 small">
                Use custom scope statement instead of the builder
              </label>
            </div>

            {form.s3_useCustom ? (
              <TextareaField
                label="Custom Scope Statement"
                hint="Write your own scope statement exactly as it will appear in your ISMS documentation."
                value={form.s3_customStatement}
                placeholder="The ISMS covers…"
                rows={5}
                onChange={(v) => update('s3_customStatement', v)}
              />
            ) : (
              <>
                <ScopeSentencePreview
                  services={form.s3_services}
                  departments={form.s3_departments}
                  locations={form.s3_locations}
                  technology={form.s3_technology}
                />
                <div className="row g-3 mb-2">
                  <div className="col-md-6 np-field">
                    <label>Business services / activities</label>
                    <input type="text" className="np-input"
                      value={form.s3_services}
                      placeholder="e.g. development, operation and support of the SaaS platform"
                      onChange={(e) => update('s3_services', e.target.value)} />
                  </div>
                  <div className="col-md-6 np-field">
                    <label>Departments / teams</label>
                    <input type="text" className="np-input"
                      value={form.s3_departments}
                      placeholder="e.g. Engineering, Security, Operations"
                      onChange={(e) => update('s3_departments', e.target.value)} />
                  </div>
                  <div className="col-md-6 np-field">
                    <label>Locations / delivery model</label>
                    <input type="text" className="np-input"
                      value={form.s3_locations}
                      placeholder="e.g. London (HQ) and remote, EU only"
                      onChange={(e) => update('s3_locations', e.target.value)} />
                  </div>
                  <div className="col-md-6 np-field">
                    <label>Technology platforms</label>
                    <input type="text" className="np-input"
                      value={form.s3_technology}
                      placeholder="e.g. AWS (eu-west-1), GitHub, Okta"
                      onChange={(e) => update('s3_technology', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </SectionCard>

          {/* ── Section 4 – Organisational Boundaries ────────────────────── */}
          <SectionCard {...SECTIONS[3]} complete={sectionComplete(form, '4')}>
            <p className="np-sub mb-3">
              Define which people, departments and roles are within the scope of the ISMS.
            </p>
            <ReferencePanel items={[
              { label: 'People',       count: people.length,      to: '/people',      icon: 'ri-user-3-line',   loading: loadingPeople },
              { label: 'Departments',  count: departments.length, to: '/departments', icon: 'ri-building-line', loading: loadingDepts },
            ]} />
            <TextareaField
              label="Boundary notes"
              hint="Clarify which teams, roles or third-party personnel are within scope and any internal exclusions."
              value={form.s4_notes}
              placeholder="e.g. All permanent employees and contractors who access production systems are within scope. Board members and non-technical functions are out of scope..."
              rows={5}
              onChange={(v) => update('s4_notes', v)}
            />
          </SectionCard>

          {/* ── Section 5 – Physical Scope ───────────────────────────────── */}
          <SectionCard {...SECTIONS[4]} complete={sectionComplete(form, '5')}>
            <p className="np-sub mb-3">
              Physical locations and hosting environments covered by the ISMS.
            </p>
            <ReferencePanel items={[
              { label: 'Physical Sites', count: sites.length, to: '/physical-sites', icon: 'ri-map-pin-2-line', loading: loadingSites },
            ]} />
            {sites.length > 0 && (
              <div className="mb-3">
                <div className="np-hint fw-semibold mb-1">Registered sites:</div>
                <div className="d-flex flex-wrap gap-1">
                  {sites.map((s: any) => (
                    <span key={s.siteId} className="np-chip np-tone-neutral">
                      {s.siteName || s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <TextareaField
              label="Additional sites and hosting environments"
              hint="Include cloud regions, co-location facilities or other hosting environments not captured in Physical Sites."
              value={form.s5_additionalSites}
              placeholder="e.g. AWS eu-west-1 (primary), AWS eu-west-2 (DR), London co-lo (Tier 3)..."
              rows={3}
              onChange={(v) => update('s5_additionalSites', v)}
            />
            <TextareaField
              label="Physical scope notes"
              hint="Any additional context about the physical boundaries."
              value={form.s5_notes}
              rows={3}
              onChange={(v) => update('s5_notes', v)}
            />
          </SectionCard>

          {/* ── Section 6 – Information Assets ──────────────────────────── */}
          <SectionCard {...SECTIONS[5]} complete={sectionComplete(form, '6')}>
            <p className="np-sub mb-3">
              Reference to asset registers and the categories of information processed within scope.
            </p>
            <ReferencePanel items={[
              { label: 'Asset Register', count: assets.length, to: '/assets', icon: 'ri-database-2-line', loading: loadingAssets },
            ]} />
            <TextareaField
              label="Data types handled"
              hint="List the categories of personal, financial, operational or classified data processed within scope."
              value={form.s6_dataTypes}
              placeholder="e.g. Customer PII (names, email, address), payment card data (PCI-DSS in scope), employee HR records, proprietary source code..."
              rows={4}
              onChange={(v) => update('s6_dataTypes', v)}
            />
            <TextareaField
              label="Information asset notes"
              hint="Add any supplementary notes about asset management approach or data classification."
              value={form.s6_notes}
              rows={3}
              onChange={(v) => update('s6_notes', v)}
            />
          </SectionCard>

          {/* ── Section 7 – Technology Scope ─────────────────────────────── */}
          <SectionCard {...SECTIONS[6]} complete={sectionComplete(form, '7')}>
            <p className="np-sub mb-3">
              Systems, networks, infrastructure and cloud platforms within the ISMS boundary.
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <TextareaField
                  label="Systems and applications"
                  value={form.s7_systems}
                  placeholder="e.g. CRM (Salesforce), ERP (SAP), source code (GitHub), CI/CD (GitHub Actions)..."
                  rows={3}
                  onChange={(v) => update('s7_systems', v)}
                />
              </div>
              <div className="col-md-6">
                <TextareaField
                  label="Networks"
                  value={form.s7_networks}
                  placeholder="e.g. Corporate LAN, VPN (WireGuard), zero-trust remote access (Cloudflare Access)..."
                  rows={3}
                  onChange={(v) => update('s7_networks', v)}
                />
              </div>
              <div className="col-md-6">
                <TextareaField
                  label="Infrastructure"
                  value={form.s7_infrastructure}
                  placeholder="e.g. AWS EC2, RDS (PostgreSQL), S3, IAM, CloudTrail, on-premises file servers..."
                  rows={3}
                  onChange={(v) => update('s7_infrastructure', v)}
                />
              </div>
              <div className="col-md-6">
                <TextareaField
                  label="Cloud platforms"
                  value={form.s7_cloudPlatforms}
                  placeholder="e.g. AWS (eu-west-1 primary), Microsoft 365, Google Workspace, Atlassian Cloud..."
                  rows={3}
                  onChange={(v) => update('s7_cloudPlatforms', v)}
                />
              </div>
            </div>
          </SectionCard>

          {/* ── Section 8 – Risk Management Framework ────────────────────── */}
          <SectionCard {...SECTIONS[7]} complete={sectionComplete(form, '8')}>
            <p className="np-sub mb-3">
              References to the risk and threat registers that form the ISMS risk management output.
            </p>
            <ReferencePanel items={[
              { label: 'Risk Registers',       count: riskRegs.length,   to: '/registers',              icon: 'ri-alert-line',          loading: loadingRiskR },
              { label: 'Threat Registers',     count: threatRegs.length, to: '/threat-registers',       icon: 'ri-skull-line',          loading: loadingThreatR },
              { label: 'Remediation Registers',count: remRegs.length,    to: '/remediation-registers',  icon: 'ri-tools-line',          loading: loadingRemR },
            ]} />
            <TextareaField
              label="Risk management framework notes"
              hint="Describe the risk methodology (e.g. ISO 27005, NIST RMF) and how risk acceptance criteria are set."
              value={form.s8_notes}
              placeholder="e.g. Risk assessments are conducted annually and on material change using a 5×5 likelihood/impact matrix. Risk acceptance threshold is set at 'Medium'. All residual risks above threshold require executive sign-off..."
              rows={5}
              onChange={(v) => update('s8_notes', v)}
            />
          </SectionCard>

          {/* ── Section 9 – Control Framework ───────────────────────────── */}
          <SectionCard {...SECTIONS[8]} complete={sectionComplete(form, '9')}>
            <p className="np-sub mb-3">
              References to the Statement of Applicability and control matrices that implement Annex A.
            </p>
            <ReferencePanel items={[
              { label: 'Control Matrices',           count: matrices.length, to: '/control-matrices',               icon: 'ri-grid-line',              loading: loadingMatrices },
              { label: 'Statement of Applicability', count: 1,               to: '/soa',                            icon: 'ri-file-text-line',         loading: false },
            ]} />
            <TextareaField
              label="Control framework notes"
              hint="Summarise which control frameworks are applied (e.g. ISO 27001 Annex A, SOC 2, NIS2) and the rationale for any exclusions."
              value={form.s9_notes}
              placeholder="e.g. Controls are implemented in line with ISO 27001:2022 Annex A (93 controls). The BSI C5 and NIS2 frameworks are additionally mapped. A full Statement of Applicability is maintained and reviewed annually..."
              rows={5}
              onChange={(v) => update('s9_notes', v)}
            />
          </SectionCard>

          {/* ── Section 10 – Compliance & Governance ─────────────────────── */}
          <SectionCard {...SECTIONS[9]} complete={sectionComplete(form, '10')}>
            <p className="np-sub mb-3">
              Legal obligations and security governance objectives that the ISMS must satisfy.
            </p>
            <ReferencePanel items={[
              { label: 'Legal Register',       count: legal.length,    to: '/legal-register',       icon: 'ri-government-line',     loading: loadingLegal },
              { label: 'Security Objectives',  count: secObjs.length,  to: '/security-objectives',  icon: 'ri-flag-2-line',          loading: loadingSecObjs },
            ]} />
            <TextareaField
              label="Compliance & governance notes"
              hint="Describe the governance structure (CISO, steering committee, management review) and key regulatory obligations."
              value={form.s10_notes}
              placeholder="e.g. Governance is overseen by the CISO reporting to the CEO. Management reviews occur quarterly. Key obligations include GDPR, NIS2 and customer contractual requirements..."
              rows={5}
              onChange={(v) => update('s10_notes', v)}
            />
          </SectionCard>

          {/* ── Section 11 – ISMS Records & Evidence ─────────────────────── */}
          <SectionCard {...SECTIONS[10]} complete={sectionComplete(form, '11')}>
            <p className="np-sub mb-3">
              Documented information required by ISO 27001 §7.5 and the registers that hold it.
            </p>
            <ReferencePanel items={[
              { label: 'Evidence Registers', count: evidenceRegs.length, to: '/evidence-registers', icon: 'ri-file-list-3-line', loading: loadingEvRegs },
              { label: 'Issues Registers',   count: issueRegs.length,    to: '/issue-registers',    icon: 'ri-error-warning-line', loading: loadingIssRegs },
            ]} />
            <TextareaField
              label="Records management notes"
              hint="Describe retention periods, storage locations and the approach to document control."
              value={form.s11_notes}
              placeholder="e.g. All ISMS documented information is maintained in Nimbus with a 3-year retention policy. Policies are version-controlled and approved by the CISO. Evidence of controls is collected continuously and reviewed in quarterly management reviews..."
              rows={5}
              onChange={(v) => update('s11_notes', v)}
            />
          </SectionCard>

          {/* ── Section 12 – Interfaces & Dependencies ───────────────────── */}
          <SectionCard {...SECTIONS[11]} complete={sectionComplete(form, '12')}>
            <p className="np-sub mb-3">
              External parties, suppliers and systems that interface with, or are depended upon by, the ISMS boundary.
              Include SaaS services and outsourced processing.
            </p>

            {/* ── Third Parties ── */}
            <div className="mb-4">
              <h6 className="fw-semibold mb-1">Third Parties</h6>
              <div className="np-sub mb-2">Consultants, auditors, managed service providers. Searches your Interested Parties register.</div>
              <div className="position-relative mb-2">
                <input
                  className="np-input"
                  placeholder="Search Interested Parties…"
                  value={tpSearch}
                  autoComplete="off"
                  onChange={(e) => { setTpSearch(e.target.value); setShowTpDrop(true); }}
                  onFocus={() => setShowTpDrop(true)}
                  onBlur={() => setTimeout(() => setShowTpDrop(false), 150)}
                />
                {showTpDrop && (() => {
                  const q = tpSearch.toLowerCase();
                  const already = new Set((form.s12_thirdPartyRefs || []).map((r) => r.id));
                  const matches = parties
                    .filter((p) => !already.has(p.partyId) && (!q || (p.name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q)))
                    .slice(0, 8);
                  if (!matches.length) return null;
                  return (
                    <div className="np-popover" style={{ right: 0, maxHeight: 220, overflowY: 'auto' }}>
                      {matches.map((p) => (
                        <div
                          key={p.partyId}
                          className="np-popover-item"
                          onMouseDown={() => {
                            update('s12_thirdPartyRefs', [...(form.s12_thirdPartyRefs || []), { id: p.partyId, name: p.name, category: p.category }]);
                            setTpSearch(''); setShowTpDrop(false);
                          }}
                        >
                          <i className="ri-group-line text-primary" />
                          <span className="fw-medium">{p.name}</span>
                          {p.category && <span style={{ color: 'var(--bs-secondary-color)' }} className="ms-1">· {p.category}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {(form.s12_thirdPartyRefs || []).length > 0 && (
                <NimbusTable wrapperClassName="mb-2" thead={<tr><th>Name</th><th>Category</th><th style={{ width: 36 }}></th></tr>}>
                      {(form.s12_thirdPartyRefs || []).map((r) => (
                        <tr key={r.id}>
                          <td className="small">{r.name}</td>
                          <td className="small" style={{ color: 'var(--bs-secondary-color)' }}>{r.category || '—'}</td>
                          <td className="text-center">
                            <button className="np-icon-btn np-icon-btn-danger" onClick={() => update('s12_thirdPartyRefs', (form.s12_thirdPartyRefs || []).filter((x) => x.id !== r.id))}>
                              <i className="ri-close-line" />
                            </button>
                          </td>
                        </tr>
                      ))}
                </NimbusTable>
              )}
              <TextareaField
                label="Third party notes"
                hint="Any additional context not captured by linked records."
                value={form.s12_thirdParties}
                placeholder="e.g. External ISMS auditor (BSI Group), pen-testing (NCC Group), SOC monitoring (Arctic Wolf)..."
                rows={2}
                onChange={(v) => update('s12_thirdParties', v)}
              />
            </div>

            <hr className="my-3" />

            {/* ── Suppliers ── */}
            <div className="mb-4">
              <h6 className="fw-semibold mb-1">Suppliers</h6>
              <div className="np-sub mb-2">Technology and service suppliers under formal contract. Searches your Interested Parties register.</div>
              <div className="position-relative mb-2">
                <input
                  className="np-input"
                  placeholder="Search Interested Parties…"
                  value={suppSearch}
                  autoComplete="off"
                  onChange={(e) => { setSuppSearch(e.target.value); setShowSuppDrop(true); }}
                  onFocus={() => setShowSuppDrop(true)}
                  onBlur={() => setTimeout(() => setShowSuppDrop(false), 150)}
                />
                {showSuppDrop && (() => {
                  const q = suppSearch.toLowerCase();
                  const already = new Set((form.s12_supplierRefs || []).map((r) => r.id));
                  const matches = parties
                    .filter((p) => !already.has(p.partyId) && (!q || (p.name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q)))
                    .slice(0, 8);
                  if (!matches.length) return null;
                  return (
                    <div className="np-popover" style={{ right: 0, maxHeight: 220, overflowY: 'auto' }}>
                      {matches.map((p) => (
                        <div
                          key={p.partyId}
                          className="np-popover-item"
                          onMouseDown={() => {
                            update('s12_supplierRefs', [...(form.s12_supplierRefs || []), { id: p.partyId, name: p.name, category: p.category }]);
                            setSuppSearch(''); setShowSuppDrop(false);
                          }}
                        >
                          <i className="ri-group-line text-warning" />
                          <span className="fw-medium">{p.name}</span>
                          {p.category && <span style={{ color: 'var(--bs-secondary-color)' }} className="ms-1">· {p.category}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {(form.s12_supplierRefs || []).length > 0 && (
                <NimbusTable wrapperClassName="mb-2" thead={<tr><th>Name</th><th>Category</th><th style={{ width: 36 }}></th></tr>}>
                      {(form.s12_supplierRefs || []).map((r) => (
                        <tr key={r.id}>
                          <td className="small">{r.name}</td>
                          <td className="small" style={{ color: 'var(--bs-secondary-color)' }}>{r.category || '—'}</td>
                          <td className="text-center">
                            <button className="np-icon-btn np-icon-btn-danger" onClick={() => update('s12_supplierRefs', (form.s12_supplierRefs || []).filter((x) => x.id !== r.id))}>
                              <i className="ri-close-line" />
                            </button>
                          </td>
                        </tr>
                      ))}
                </NimbusTable>
              )}
              <TextareaField
                label="Supplier notes"
                hint="Any additional context not captured by linked records."
                value={form.s12_suppliers}
                placeholder="e.g. AWS (IaaS), Microsoft (M365 + Azure AD), Stripe (payment processing), Twilio (SMS MFA)..."
                rows={2}
                onChange={(v) => update('s12_suppliers', v)}
              />
            </div>

            <hr className="my-3" />

            {/* ── External Systems ── */}
            <div className="mb-2">
              <h6 className="fw-semibold mb-1">External Systems</h6>
              <div className="np-sub mb-2">APIs, data feeds and integration points outside the organisational boundary. Searches your Assets register.</div>
              <div className="position-relative mb-2">
                <input
                  className="np-input"
                  placeholder="Search Assets…"
                  value={extSearch}
                  autoComplete="off"
                  onChange={(e) => { setExtSearch(e.target.value); setShowExtDrop(true); }}
                  onFocus={() => setShowExtDrop(true)}
                  onBlur={() => setTimeout(() => setShowExtDrop(false), 150)}
                />
                {showExtDrop && (() => {
                  const q = extSearch.toLowerCase();
                  const already = new Set((form.s12_externalSystemRefs || []).map((r) => r.id));
                  const matches = assets
                    .filter((a) => !already.has(a.assetId) && (!q || (a.name || '').toLowerCase().includes(q) || (a.assetType || a.type || '').toLowerCase().includes(q)))
                    .slice(0, 8);
                  if (!matches.length) return null;
                  return (
                    <div className="np-popover" style={{ right: 0, maxHeight: 220, overflowY: 'auto' }}>
                      {matches.map((a) => (
                        <div
                          key={a.assetId}
                          className="np-popover-item"
                          onMouseDown={() => {
                            update('s12_externalSystemRefs', [...(form.s12_externalSystemRefs || []), { id: a.assetId, name: a.name, assetType: a.assetType || a.type }]);
                            setExtSearch(''); setShowExtDrop(false);
                          }}
                        >
                          <i className="ri-server-2-line text-info" />
                          <span className="fw-medium">{a.name}</span>
                          {(a.assetType || a.type) && <span style={{ color: 'var(--bs-secondary-color)' }} className="ms-1">· {a.assetType || a.type}</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {(form.s12_externalSystemRefs || []).length > 0 && (
                <NimbusTable wrapperClassName="mb-2" thead={<tr><th>Name</th><th>Type</th><th style={{ width: 36 }}></th></tr>}>
                      {(form.s12_externalSystemRefs || []).map((r) => (
                        <tr key={r.id}>
                          <td className="small">{r.name}</td>
                          <td className="small" style={{ color: 'var(--bs-secondary-color)' }}>{r.assetType || '—'}</td>
                          <td className="text-center">
                            <button className="np-icon-btn np-icon-btn-danger" onClick={() => update('s12_externalSystemRefs', (form.s12_externalSystemRefs || []).filter((x) => x.id !== r.id))}>
                              <i className="ri-close-line" />
                            </button>
                          </td>
                        </tr>
                      ))}
                </NimbusTable>
              )}
              <TextareaField
                label="External systems notes"
                hint="Any additional context not captured by linked records."
                value={form.s12_externalSystems}
                placeholder="e.g. Companies House API (read-only), HMRC Making Tax Digital, open banking APIs (read-only)..."
                rows={2}
                onChange={(v) => update('s12_externalSystems', v)}
              />
            </div>
          </SectionCard>

          {/* ── Section 13 – Exclusions ──────────────────────────────────── */}
          <SectionCard {...SECTIONS[12]} complete={sectionComplete(form, '13')}>
            <div className="np-banner np-tone-warning mb-3">
              <i className="ri-alert-line"></i>
              <div className="small">
                <strong>Auditor note:</strong> Exclusions from Annex A must be explicitly justified in the Statement of
                Applicability. This section should document functional or physical exclusions from the ISMS boundary,
                not control exclusions.
              </div>
            </div>
            <TextareaField
              label="Exclusion statement"
              hint="Explicitly state all systems, locations, business functions, departments or activities that are outside the scope of this ISMS. Each exclusion should be justified."
              value={form.s13_exclusions}
              placeholder="e.g.&#10;1. The legacy billing system (EOL Q3 2026) is excluded pending decommission. Justification: system is isolated from production network with no customer data access.&#10;2. The US subsidiary (Acme Inc.) is excluded as it operates under a separate ISMS initiated in 2025.&#10;3. Marketing activities (social media, PR) are excluded as they do not process in-scope data."
              rows={8}
              onChange={(v) => update('s13_exclusions', v)}
            />
          </SectionCard>

          {/* ── Bottom save bar ────────────────────────────────────────────── */}
          <div className="d-flex justify-content-end align-items-center gap-3 py-3">
            {isDirty && (
              <span className="np-sub">
                <i className="ri-error-warning-line me-1 text-warning" />You have unsaved changes
              </span>
            )}
            <button
              className="np-btn np-btn-primary"
              onClick={handleSave}
              disabled={saveMutation.isPending || !isDirty}
            >
              {saveMutation.isPending
                ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                : <><i className="ri-save-line me-2" />Save ISMS Scope</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
